// src/components/NotificationPrompt.js
import React, { useState, useEffect } from 'react';
import { Bell, Loader, Lock } from 'lucide-react';
import { requestNotificationPermission } from '../firebase';
import { upsertSubscriberToken } from '../utils/airtable';

const STORAGE_KEY = 'votergо_notifications';

const inputStyle = {
  background: '#f5f5f5', border: '0.5px solid #ddd', borderRadius: 6,
  padding: '8px 10px', color: '#1a1a1a', fontSize: 13, width: '100%',
};

const Overlay = ({ children }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1001,
  }}>
    <div style={{
      width: 320, background: 'white', borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)', padding: 28,
      textAlign: 'center',
    }}>
      <img src="/logo.png" alt="VoterGO" style={{ width: 64, height: 64, display: 'block', margin: '0 auto 16px' }} />
      {children}
    </div>
  </div>
);

const NotificationPrompt = ({ onTokenReceived, county }) => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState('prompt'); // 'prompt', 'form', 'loading'
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', zipCode: ''
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'enabled' || stored === 'dismissed') return;
    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'dismissed');
    setShow(false);
  };

  const handleEnable = async (subscriberInfo) => {
    setLoading(true);
    setStep('loading');
    try {
      const token = await requestNotificationPermission();
      if (token) {
        // Save to Airtable Subscribers table with identity info — de-duped
        // by token so re-enabling after being deactivated can't create a
        // second row for the same device.
        await upsertSubscriberToken(token, {
          'First Name': subscriberInfo.firstName || '',
          'Last Name': subscriberInfo.lastName || '',
          Email: subscriberInfo.email || '',
          Phone: subscriberInfo.phone || '',
          'Zip Code': subscriberInfo.zipCode || '',
        });
        localStorage.setItem(STORAGE_KEY, 'enabled');
        setShow(false);
        if (onTokenReceived) onTokenReceived(token);
      } else {
        setShow(false);
      }
    } catch (err) {
      console.log('Notification error:', err);
      setShow(false);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  // Step 1 — Initial prompt
  if (step === 'prompt') {
    return (
      <Overlay>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#182B45', marginBottom: 8 }}>
          Stay informed
        </div>
        <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5, marginBottom: 20 }}>
          Get alerts for election reminders, new candidates, events, and ballot updates.
        </div>
        <button
          onClick={() => setStep('form')}
          style={{
            width: '100%', background: '#ae3030', color: 'white', border: 'none',
            borderRadius: 6, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 12,
          }}
        >
          <Bell size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enable
        </button>
        <button
          onClick={handleDismiss}
          style={{ background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
        >
          Not now
        </button>
      </Overlay>
    );
  }

  // Step 2 — Identity form
  if (step === 'form') {
    return (
      <Overlay>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#182B45', marginBottom: 8 }}>
          Enable notifications
        </div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 16, lineHeight: 1.5 }}>
          Optional — tell us who you are so we can personalize your notifications. You can skip this and stay anonymous.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8, textAlign: 'left' }}>
          <input
            type="text"
            placeholder="First name"
            value={form.firstName}
            onChange={e => setForm({ ...form, firstName: e.target.value })}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="Last name"
            value={form.lastName}
            onChange={e => setForm({ ...form, lastName: e.target.value })}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 8, textAlign: 'left' }}>
          <input
            type="email"
            placeholder="Email (optional)"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 8, textAlign: 'left' }}>
          <input
            type="tel"
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 16, textAlign: 'left' }}>
          <input
            type="text"
            placeholder="Zip code (optional)"
            maxLength={5}
            value={form.zipCode}
            onChange={e => setForm({ ...form, zipCode: e.target.value })}
            style={inputStyle}
          />
        </div>

        <button
          onClick={() => handleEnable(form)}
          disabled={loading}
          style={{
            width: '100%', background: '#ae3030', color: 'white', border: 'none',
            borderRadius: 6, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 12,
          }}
        >
          {loading
            ? <><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enabling...</>
            : <><Bell size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enable notifications</>}
        </button>
        <button
          onClick={() => handleEnable({})}
          disabled={loading}
          style={{ background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', marginBottom: 12 }}
        >
          Skip
        </button>

        <div style={{ fontSize: 10, color: '#999' }}>
          <Lock size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Your information is only used by the {county?.orgName || 'sponsoring county party'}
        </div>
      </Overlay>
    );
  }

  return null;
};

export default NotificationPrompt;
